import { StackScreenProps } from "@react-navigation/stack";
import { useState } from "react";
import { Spacings, View } from "react-native-ui-lib";
import {
  CheckoutLaybyRootScreenParamList,
  CheckoutRootParamList,
} from "../../../../types";
import { useGlobalSettingsProvider } from "../../../../utilities/global-settings/hooks";
import {
  moneyToFormattedString,
  valueAsMoney,
} from "../../../../utilities/money";
import { useCheckoutContextProvider } from "../../utils/checkout";
import { usePaymentControllerProvider } from "../../utils/payment-controller";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  Page,
  TextField,
  Divider,
  Text,
  Stack,
  DateTimePicker,
  Button,
} from "@towersystems/roam-common-native";
import PaymentMethod from "../../../../utilities/data/model/PaymentMethod";
import { useFocusEffect } from "@react-navigation/native";
import { formatPercentString } from "../../../../utilities/format-percent-string";
import { compareLessThanWithTolerance } from "../../../../utilities/compare-with-tolerance";
import React from "react";
import { SelectablePaymentMethodButtonList } from "../../components/SelectablePaymentMethodButtonList";
import { onlineOnlyIds } from "../SelectPaymentMethodScreen";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useTranslation } from "../../../../utilities/i18n";
import { SaleState } from "../../../../utilities/data/model/Sale";

export type CheckoutLaybyScreenProps = StackScreenProps<
  CheckoutLaybyRootScreenParamList & CheckoutRootParamList,
  "CheckoutLaybyScreen"
>;

export const CheckoutLaybyScreen = ({
  navigation,
}: CheckoutLaybyScreenProps) => {
  const online = useOnline();
  const { t } = useTranslation("pos");
  const { sale, selectablePaymentMethods, balance } =
    useCheckoutContextProvider();
  const [minDepositAmount, setMinDepositAmount] = useState(0);
  const [expectedPickupDateInput, setExpectedPickupDateInput] = useState(
    sale.expectedPickupDate?.toISOString() || ""
  );

  const shouldFocus = useHasTransitionEnded(navigation);

  const globalSettings = useGlobalSettingsProvider();

  const { tenderedAmountInput, validTenderedAmount, setTenderedAmountInput } =
    usePaymentControllerProvider();

  const { minLaybySpend, defaultLaybyDepositPercentage } = globalSettings;

  const normalizedValidTenderedAmount =
    validTenderedAmount &&
    compareLessThanWithTolerance(
      minDepositAmount,
      Number(tenderedAmountInput)
    ) &&
    compareLessThanWithTolerance(Number(tenderedAmountInput), balance);

  const validPickupDate = React.useMemo(() => {
    if (!expectedPickupDateInput) return true;
    // check if the date is valid only
    const date = new Date(expectedPickupDateInput);
    return !isNaN(date.getTime());
  }, [expectedPickupDateInput]);

  const handleOnPaymentSkip = async () => {
    if (validPickupDate && expectedPickupDateInput) {
      sale.setExpectedPickupDate(new Date(expectedPickupDateInput));
    }
    await sale.transitionToState(SaleState.LAYBY_CLOSED);
    navigation.reset({
      index: 0,
      routes: [{ name: "CheckoutSummaryRootScreen" }],
    });
  };

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.skip_payment")}
          isAction
          onPress={handleOnPaymentSkip}
          size="compact"
          disabled={!validPickupDate}
        ></Button>
      ),
    });
  }, [handleOnPaymentSkip, validPickupDate]);

  useFocusEffect(
    React.useCallback(() => {
      if (balance < minLaybySpend || !sale.customer.id) {
        navigation.pop();
      }
      const _minDepositAmount = balance * (defaultLaybyDepositPercentage / 100);
      setMinDepositAmount(_minDepositAmount);
      setTenderedAmountInput(valueAsMoney(_minDepositAmount.toString()));
    }, [balance, minLaybySpend])
  );

  const handleOnPressAll = async (item: PaymentMethod) => {
    await sale.setExpectedPickupDate(new Date(expectedPickupDateInput));
    navigation.navigate("PaymentRootScreen", {
      screen: "PaymentScreen",
      paymentMethodId: item.id,
      tenderedAmount: Number(tenderedAmountInput),
      minTenderAmount: Number(tenderedAmountInput),
    });
  };

  const overTendered =
    validTenderedAmount && Number(tenderedAmountInput) > balance;

  const paymentsDisabled =
    !validPickupDate || !normalizedValidTenderedAmount || overTendered;

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      title={`Start Layby`}
      secondaryTitle={`Minimum deposit of ${formatPercentString(
        defaultLaybyDepositPercentage.toString()
      )} (${moneyToFormattedString(minDepositAmount)}) required`}
    >
      <Stack vertical>
        <View>
          <Text
            style={{
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s4,
            }}
          >
            Enter deposit amount
          </Text>
          <View>
            <TextField
              onChangeText={setTenderedAmountInput}
              value={tenderedAmountInput}
              autoFocus={shouldFocus}
              formatterType={
                normalizedValidTenderedAmount ? "money" : undefined
              }
              validationMessage={
                !normalizedValidTenderedAmount
                  ? `Enter number between ${moneyToFormattedString(
                      minDepositAmount.toString()
                    )} and ${moneyToFormattedString(balance)}`
                  : ""
              }
            />
          </View>
        </View>
        <View>
          <Text
            style={{
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s4,
            }}
          >
            Optional Expected Pickup Date
          </Text>
          <View>
            <DateTimePicker
              value={
                expectedPickupDateInput
                  ? new Date(expectedPickupDateInput)
                  : undefined
              }
              onChange={(date) => {
                setExpectedPickupDateInput(date.toISOString());
              }}
              placeholder=""
            />
          </View>
        </View>
        <Divider />
        <View>
          <Text
            style={{
              textTransform: "uppercase",
              fontWeight: "bold",
              marginBottom: Spacings.s4,
            }}
          >
            Select Payment Method
          </Text>
          <SelectablePaymentMethodButtonList
            wrapped
            items={selectablePaymentMethods}
            allDisabled={paymentsDisabled}
            onPressAll={(...p) => handleOnPressAll(...p)}
            disabledFn={(item) =>
              normalizedValidTenderedAmount &&
              ((balance == 0 && item.payType.id !== "1") ||
                (Number(tenderedAmountInput) == 0 && item.payType.id !== "1") ||
                (onlineOnlyIds.includes(item.payType.id) && !online))
            }
          />
        </View>
      </Stack>
    </Page>
  );
};
